import type { AxiosError } from 'axios';
import { authClient } from './client';
import type {
  PhotoAngle,
  PhotoQueueExtraPhoto,
  PhotoQueueFilter,
  PhotoQueueItem,
  PhotoQueueListPayload,
  PhotoQueueSlot,
  PhotoQueueSummary,
  PhotoUploadAngle,
} from '../types/photoQueue';
import { NEW_ITEM_ANGLES, USED_ITEM_ANGLES } from '../types/photoQueue';

/**
 * Backend contract — implementar en el API:
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ### GET /api/admin/photo-queue
 * Auth:   Bearer token (solo admin)
 * ─────────────────────────────────────────────────────────────────────────────
 * Query params (todos opcionales):
 *   - search    (string)                  búsqueda parcial en título, SKU y nombre de consignador
 *   - filter    ('all' | 'new' | 'used')  default 'all'
 *   - page      (number)                  default 1
 *   - pageSize  (number)                  default 20
 *
 * Solo devuelve ítems con al menos una foto pendiente.
 * `isNew: true`  → solo el ángulo 'front' es requerido  (1 foto total).
 * `isNew: false` → ángulos 'lateral_exterior', 'lateral_interior', 'front_45',
 *                  'back', 'outsole' son requeridos       (5 fotos total).
 *
 * Respuesta JSON:
 * ```json
 * {
 *   "message": "OK",
 *   "data": {
 *     "items": [
 *       {
 *         "id": 1,
 *         "artId": "UU-00042",
 *         "title": "Nike Dunk Low 'Panda'",
 *         "consignorName": "Carlos Rodriguez",
 *         "sizeCm": "28.5",
 *         "isNew": false,
 *         "photos": [
 *           { "angle": "lateral_exterior", "url": "https://cdn.example.com/photo1.jpg" },
 *           { "angle": "lateral_interior", "url": null },
 *           { "angle": "front_45",         "url": null },
 *           { "angle": "back",             "url": null },
 *           { "angle": "outsole",          "url": null }
 *         ],
 *         "uploadedCount": 1,
 *         "totalRequired": 5,
 *         "extraPhotos": [{ "id": 103, "url": "https://..." }]
 *       }
 *     ],
 *     "total": 24,
 *     "page": 1,
 *     "pageSize": 20,
 *     "summary": {
 *       "totalPending": 24,
 *       "newPending": 9,
 *       "usedPending": 15
 *     }
 *   }
 * }
 * ```
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ### POST /api/admin/photo-queue/:inventoryId/photos/:angle
 * Auth:          Bearer token (solo admin)
 * Content-Type:  multipart/form-data
 * ─────────────────────────────────────────────────────────────────────────────
 * Path params:
 *   - inventoryId  (number)  ID del item de inventario
 *   - angle        (string)  ángulo requerido ('front', 'front_45', …) o 'extra' (agrega foto opcional)
 *
 * Body field:
 *   - image  (File)  JPG, PNG o WEBP.
 *   Ángulos requeridos: subir al mismo ángulo reemplaza la foto anterior.
 *   'extra': siempre agrega una nueva imagen (no reemplaza extras previas).
 *
 * Cuando todos los ángulos requeridos de un ítem tienen foto, el ítem debe
 * salir de la cola (cambiar su estado interno, p. ej. a 'Activo' o similar).
 *
 * Respuesta JSON:
 * ```json
 * {
 *   "message": "Foto guardada exitosamente",
 *   "data": { "url": "https://cdn.example.com/uploaded.jpg" }
 * }
 * ```
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ### PATCH /api/admin/inventory/:inventoryId/photo-requirements-waived
 * Auth:  Bearer (solo admin) · `Content-Type: application/json`
 * Body: `{ "waived": true | false }` (boolean, no string)
 * Solo productos **usados**; si es nuevo o body inválido → 400.
 * Respuesta 200: `{ "data": { "inventoryId", "photoRequirementsWaived" } }`
 */

function parseBooleanish(raw: unknown): boolean {
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw === 1;
  const t = String(raw ?? '')
    .trim()
    .toLowerCase();
  return t === 'true' || t === '1' || t === 'yes' || t === 'si';
}

const VALID_ANGLES = new Set<string>([
  'front',
  'lateral_exterior',
  'lateral_interior',
  'front_45',
  'back',
  'outsole',
]);

function parsePhotoExtra(raw: unknown): PhotoQueueExtraPhoto | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  const urlRaw = r.url ?? r.imageUrl ?? r.photoUrl;
  const url = typeof urlRaw === 'string' && urlRaw.trim() ? urlRaw.trim() : '';
  if (!Number.isFinite(id) || id <= 0 || !url) return null;
  return { id, url };
}

function parsePhotoSlot(raw: unknown): PhotoQueueSlot | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const angle = String(r.angle ?? '').trim();
  if (!VALID_ANGLES.has(angle)) return null;
  const urlRaw = r.url ?? r.imageUrl ?? r.photoUrl;
  const url = typeof urlRaw === 'string' && urlRaw.trim() ? urlRaw.trim() : null;
  return { angle: angle as PhotoAngle, url };
}

function parsePhotoQueueItem(raw: unknown): PhotoQueueItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id)) return null;

  const isNew = (() => {
    const v = r.isNew ?? r.is_new ?? r.isNuevo;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    return false;
  })();

  const rawPhotos = r.photos ?? r.images ?? [];
  const parsedPhotos = Array.isArray(rawPhotos)
    ? rawPhotos.map(parsePhotoSlot).filter((p): p is PhotoQueueSlot => p !== null)
    : [];

  const requiredAngles = isNew ? NEW_ITEM_ANGLES : USED_ITEM_ANGLES;
  const photosByAngle = new Map<PhotoAngle, string | null>(
    parsedPhotos.map((p) => [p.angle, p.url])
  );

  const photos: PhotoQueueSlot[] = requiredAngles.map((angle) => ({
    angle,
    url: photosByAngle.get(angle) ?? null,
  }));

  const rawExtras = r.extraPhotos ?? r.extra_photos ?? [];
  const extraPhotos = Array.isArray(rawExtras)
    ? rawExtras.map(parsePhotoExtra).filter((p): p is PhotoQueueExtraPhoto => p !== null)
    : [];

  const uploadedFromApi = Number(r.uploadedCount ?? r.uploaded_count);
  const totalFromApi = Number(r.totalRequired ?? r.total_required);
  const uploadedCount = Number.isFinite(uploadedFromApi)
    ? uploadedFromApi
    : photos.filter((p) => p.url !== null).length;
  const totalRequired = Number.isFinite(totalFromApi) ? totalFromApi : requiredAngles.length;

  return {
    id,
    artId: (() => {
      const v = r.artId ?? r.art_id;
      if (v == null || v === '') return null;
      const s = String(v).trim();
      return s || null;
    })(),
    title: String(r.title ?? '').trim(),
    consignorName: String(r.consignorName ?? r.consignor_name ?? '').trim(),
    sizeCm: (() => {
      const v = r.sizeCm ?? r.size_cm ?? r.size;
      return v != null && String(v).trim() ? String(v).trim() : null;
    })(),
    isNew,
    photoRequirementsWaived: parseBooleanish(
      r.photoRequirementsWaived ?? r.photo_requirements_waived
    ),
    photos,
    extraPhotos,
    uploadedCount,
    totalRequired,
  };
}

function parseSummary(raw: unknown): PhotoQueueSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const totalPending = Number(r.totalPending ?? r.total_pending);
  const newPending = Number(r.newPending ?? r.new_pending);
  const usedPending = Number(r.usedPending ?? r.used_pending);
  if (
    !Number.isFinite(totalPending) ||
    !Number.isFinite(newPending) ||
    !Number.isFinite(usedPending)
  ) {
    return null;
  }
  return { totalPending, newPending, usedPending };
}

function parseListPayload(
  body: unknown,
  fallbackPage: number,
  fallbackPageSize: number
): PhotoQueueListPayload {
  const empty: PhotoQueueListPayload = {
    items: [],
    total: 0,
    page: fallbackPage,
    pageSize: fallbackPageSize,
    summary: null,
  };
  if (!body || typeof body !== 'object') return empty;
  const o = body as Record<string, unknown>;
  const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
    string,
    unknown
  >;
  const rawItems = inner.items;
  const items = Array.isArray(rawItems)
    ? rawItems.map(parsePhotoQueueItem).filter((x): x is PhotoQueueItem => x !== null)
    : [];
  const total =
    typeof inner.total === 'number' && Number.isFinite(inner.total) ? inner.total : items.length;
  const page = typeof inner.page === 'number' && inner.page >= 1 ? inner.page : fallbackPage;
  const pageSize =
    typeof inner.pageSize === 'number' && inner.pageSize >= 1 ? inner.pageSize : fallbackPageSize;
  return { items, total, page, pageSize, summary: parseSummary(inner.summary) };
}

export interface PhotoQueueListQuery {
  search?: string;
  filter?: PhotoQueueFilter;
  page?: number;
  pageSize?: number;
}

export type PhotoQueueListResult =
  | { ok: true; data: PhotoQueueListPayload }
  | { ok: false; error: string };

export async function fetchPhotoQueue(
  query: PhotoQueueListQuery = {}
): Promise<PhotoQueueListResult> {
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? 20;
  try {
    const params: Record<string, string | number> = { page, pageSize };
    if (query.search?.trim()) params.search = query.search.trim();
    if (query.filter && query.filter !== 'all') params.filter = query.filter;
    const { data } = await authClient.get<unknown>('/api/admin/photo-queue', { params });
    return { ok: true, data: parseListPayload(data, page, pageSize) };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404
        ? 'El endpoint de cola de fotos aún no está disponible.'
        : null) ??
      'No se pudo cargar la cola de fotos.';
    return { ok: false, error: msg };
  }
}

export type PhotoUploadResult = { ok: true; url: string } | { ok: false; error: string };

export async function uploadPhotoQueueImage(
  inventoryId: number,
  angle: PhotoUploadAngle,
  file: File
): Promise<PhotoUploadResult> {
  try {
    const fd = new FormData();
    fd.append('image', file);
    const { data } = await authClient.post<unknown>(
      `/api/admin/photo-queue/${inventoryId}/photos/${angle}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const o = data as Record<string, unknown>;
    const inner = (o?.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
      string,
      unknown
    >;
    const url = String(inner?.url ?? inner?.imageUrl ?? '').trim();
    if (!url) return { ok: false, error: 'La respuesta no incluye la URL de la imagen.' };
    return { ok: true, url };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'El endpoint de subida de foto no está disponible.' : null) ??
      'No se pudo subir la foto.';
    return { ok: false, error: msg };
  }
}

export type PatchPhotoRequirementsWaivedResult =
  | { ok: true; inventoryId: number; photoRequirementsWaived: boolean }
  | { ok: false; error: string };

export async function patchPhotoRequirementsWaived(
  inventoryId: number,
  waived: boolean
): Promise<PatchPhotoRequirementsWaivedResult> {
  if (!Number.isFinite(inventoryId)) {
    return { ok: false, error: 'ID de inventario inválido.' };
  }
  try {
    const { data } = await authClient.patch<unknown>(
      `/api/admin/inventory/${inventoryId}/photo-requirements-waived`,
      { waived }
    );
    if (!data || typeof data !== 'object') {
      return { ok: false, error: 'Respuesta no válida del servidor.' };
    }
    const o = data as Record<string, unknown>;
    const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
      string,
      unknown
    >;
    const iid = Number(inner.inventoryId ?? inner.id);
    const photoRequirementsWaived = parseBooleanish(
      inner.photoRequirementsWaived ?? inner.photo_requirements_waived
    );
    if (!Number.isFinite(iid)) {
      return { ok: false, error: 'La respuesta no incluye el ID de inventario.' };
    }
    return { ok: true, inventoryId: iid, photoRequirementsWaived };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404 ? 'No se encontró el ítem de inventario.' : null) ??
      'No se pudo actualizar la exención de fotos.';
    return { ok: false, error: msg };
  }
}
